import { objectToSnakeCase } from '../../utils/background/Utils';
import { sendRequest } from './backendCommon';
import { REQUESTS, USER_EVENT_TYPE } from '../../constants/backend';
import { startMainConnection } from './backendMain';
import { IS_LEGACY_LISTENER_MODE } from '../../config/electron';
import { logUserEvent } from './backendEventLogger';
import { DO_LOGIN_REQUEST } from './actionTypes/user';

type DispatchFn = (action: unknown) => unknown;
interface ApiErrorLike {
    status?: unknown;
    message?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toAuthSuccessPayload(value: unknown): { token?: string; refreshToken?: string } {
    if (!isRecord(value)) {
        return {};
    }
    return {
        token: typeof value.token === 'string' ? value.token : undefined,
        refreshToken: typeof value.refreshToken === 'string' ? value.refreshToken : undefined
    };
}

function toApiErrorLike(value: unknown): ApiErrorLike {
    if (!isRecord(value)) {
        return {};
    }
    return {
        status: value.status,
        message: value.message
    };
}
export interface AuthStoreLike {
    refreshToken?: string | null;
    authWithPassword?: boolean;
}

export const types = {
    AUTH_FAIL: '@bg/AUTH_FAIL',
    AUTH_SUCCESS: '@bg/AUTH_SUCCESS',

    AUTH_WITH_PASSWORD_REQUIRED: '@bg/AUTH_WITH_PASSWORD_REQUIRED',

    TOKEN_REFRESH_SUCCESS: '@bg/TOKEN_REFRESH_SUCCESS',

    SYSTEM_INFO_RECEIVED: '@bg/SYSTEM_INFO_RECEIVED', // receive system info from listener
    SYSTEM_INFO_UPDATED: '@bg/SYSTEM_INFO_UPDATED' // receive updated (may be same) info from listener
};

export const loginWithCompany =
    (
        locationName: string,
        username: string,
        companySecretToken: string,
        uniqueId: string // @deprecated
    ) =>
    (dispatch: DispatchFn) => {
        const data = {
            companyName: locationName,
            username,
            token: companySecretToken,
            uniqueId
        };
        const payload = {
            body: objectToSnakeCase(data)
        };

        const onSuccess = (response: unknown) => {
            const { token, refreshToken } = toAuthSuccessPayload(response);
            dispatch({
                type: types.AUTH_SUCCESS,
                payload: {
                    token,
                    refreshToken,
                    isPasswordAuth: false
                }
            });
        };

        const onError = (error: unknown) => {
            const apiError = toApiErrorLike(error);
            dispatch({
                type: types.AUTH_FAIL,
                payload: {
                    status: apiError.status,
                    message: apiError.message
                }
            });
        };

        dispatch(
            sendRequest(REQUESTS.COMPANY_LOGIN, {
                payload,
                onSuccess,
                onError
            })
        );
    };

export const loginWithCompanyAndPassword = (locationAlias: string, username: string, password: string) => (dispatch: DispatchFn) => {
    dispatch({
        type: DO_LOGIN_REQUEST,
        payload: {
            locationName: locationAlias,
            username,
            password
        }
    });

    const payload = {
        body: {
            company_name: locationAlias,
            username,
            password
        }
    };

    const onSuccess = (response: unknown) => {
        const { token, refreshToken } = toAuthSuccessPayload(response);
        dispatch({
            type: types.AUTH_SUCCESS,
            payload: {
                token,
                refreshToken,
                isPasswordAuth: true
            }
        });
        dispatch(logUserEvent(USER_EVENT_TYPE.LOGIN));
    };

    const onError = (error: unknown) => {
        const apiError = toApiErrorLike(error);
        dispatch({
            type: types.AUTH_FAIL,
            payload: {
                status: apiError.status,
                message: apiError.message
            }
        });
    };

    dispatch(
        sendRequest(REQUESTS.COMPANY_LOGIN, {
            payload,
            onSuccess,
            onError
        })
    );
};

export const doRefreshToken = (authStore: AuthStoreLike, isLogin: boolean, dispatch: DispatchFn) => {
    // if no token then start connection to linter
    if (!authStore.refreshToken && !IS_LEGACY_LISTENER_MODE) {
        dispatch({
            type: types.AUTH_WITH_PASSWORD_REQUIRED
        });
        return;
    }

    if (IS_LEGACY_LISTENER_MODE && isLogin) {
        dispatch(startMainConnection());
        return;
    }

    const payload = {
        body: {
            refresh_token: authStore.refreshToken
        }
    };

    const onSuccess = (response: unknown) => {
        const { token } = toAuthSuccessPayload(response);
        if (isLogin) {
            dispatch({
                type: types.AUTH_SUCCESS,
                payload: {
                    token,
                    refreshToken: authStore.refreshToken,
                    isPasswordAuth: authStore.authWithPassword
                }
            });
        } else {
            dispatch({
                type: types.TOKEN_REFRESH_SUCCESS,
                payload: { token }
            });
        }
    };

    const onError = (error: unknown) => {
        const apiError = toApiErrorLike(error);
        dispatch({
            type: types.AUTH_FAIL,
            payload: {
                status: apiError.status,
                message: apiError.message
            }
        });
    };

    dispatch(
        sendRequest(REQUESTS.REFRESH_TOKEN, {
            payload,
            onSuccess,
            onError
        })
    );
};
